import { useState, useEffect } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { BarChart3, TrendingUp, TrendingDown, Car, Download, FileText, FileSpreadsheet } from "lucide-react";
import { Button } from "../components/ui/button";
import { exportCSV, exportExcel, exportPDF } from "../components/TableExport";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";

export default function ReportsPage() {
  const [bilan, setBilan] = useState([]);
  const [period, setPeriod] = useState("weekly");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchBilan = async () => {
    setLoading(true);
    try {
      let url = `/reports/bilan?period=${period}&date=${date}`;
      if (period === "custom" && customStart && customEnd) {
        url += `&start_date=${customStart}&end_date=${customEnd}`;
      }
      const res = await api.get(url);
      setBilan(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => {
    if (period === "custom") {
      if (customStart && customEnd) fetchBilan();
    } else {
      fetchBilan();
    }
  }, [period, date, customStart, customEnd]);

  const totals = bilan.reduce((acc, b) => ({
    recettes: acc.recettes + (b.total_recettes || 0),
    attendu: acc.attendu + (b.total_attendu || 0),
    reparations: acc.reparations + (b.total_reparations || 0),
    benefice: acc.benefice + (b.benefice || 0),
    jours: acc.jours + (b.jours_location || 0),
  }), { recettes: 0, attendu: 0, reparations: 0, benefice: 0, jours: 0 });

  const bilanExportCols = [
    { label: "Vehicule", accessor: (b) => `${b.vehicule?.marque || ""} ${b.vehicule?.modele || ""} (${b.vehicule?.immatriculation || ""})` },
    { label: "Periode", accessor: (b) => `${b.periode?.debut || ""} - ${b.periode?.fin || ""}` },
    { label: "Recettes", key: "total_recettes" },
    { label: "Attendu", key: "total_attendu" },
    { label: "Reparations", key: "total_reparations" },
    { label: "Benefice", key: "benefice" },
    { label: "Jours loue", key: "jours_location" },
    { label: "Jours immo.", key: "jours_immobilise" },
  ];

  return (
    <div data-testid="reports-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Bilans</h1>
          <p className="text-sm text-slate-500 mt-1">Rapports {
          period === "daily" ? "journaliers" : period === "weekly" ? "hebdomadaires" : period === "monthly" ? "mensuels" : period === "annual" ? "annuels" : "personnalises"
        }</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[170px]" data-testid="select-period"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Journalier</SelectItem>
              <SelectItem value="weekly">Hebdomadaire</SelectItem>
              <SelectItem value="monthly">Mensuel</SelectItem>
              <SelectItem value="annual">Annuel</SelectItem>
              <SelectItem value="custom">Personnalise</SelectItem>
            </SelectContent>
          </Select>
          {period !== "custom" ? (
            <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-auto" data-testid="input-report-date" />
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <Label className="text-xs text-slate-500 whitespace-nowrap">Du</Label>
                <Input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="w-auto" data-testid="input-custom-start" />
              </div>
              <div className="flex items-center gap-1">
                <Label className="text-xs text-slate-500 whitespace-nowrap">Au</Label>
                <Input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="w-auto" data-testid="input-custom-end" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Export */}
      <div className="flex justify-end mb-4">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="btn-export-bilan">
              <Download className="w-4 h-4 mr-2" />Exporter
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => exportCSV(bilan, bilanExportCols, "bilan")} data-testid="export-bilan-csv"><FileText className="w-4 h-4 mr-2" />CSV</DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportExcel(bilan, bilanExportCols, "bilan")} data-testid="export-bilan-excel"><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</DropdownMenuItem>
            <DropdownMenuItem onClick={() => exportPDF(bilan, bilanExportCols, "bilan", "Bilan")} data-testid="export-bilan-pdf"><FileText className="w-4 h-4 mr-2" />PDF</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="report-summary">
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-3"><div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-emerald-600" /></div></div>
          <p className="text-2xl font-bold text-slate-900 font-heading">{totals.recettes} Frc</p>
          <p className="text-xs text-slate-500 mt-1">Total recettes</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-3"><div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center"><TrendingDown className="w-5 h-5 text-red-600" /></div></div>
          <p className="text-2xl font-bold text-slate-900 font-heading">{totals.reparations} Frc</p>
          <p className="text-xs text-slate-500 mt-1">Total reparations</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-3"><div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center"><BarChart3 className="w-5 h-5 text-[#0F52BA]" /></div></div>
          <p className={`text-2xl font-bold font-heading ${totals.benefice >= 0 ? "text-emerald-600" : "text-red-600"}`}>{totals.benefice} Frc</p>
          <p className="text-xs text-slate-500 mt-1">Benefice net</p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3 mb-3"><div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center"><Car className="w-5 h-5 text-violet-600" /></div></div>
          <p className="text-2xl font-bold text-slate-900 font-heading">{totals.jours}</p>
          <p className="text-xs text-slate-500 mt-1">Jours de location</p>
        </div>
      </div>

      {/* Detail Table */}
      {loading ? (
        <div className="text-slate-500 text-center py-12">Chargement du bilan...</div>
      ) : (
        <div className="table-container">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50">
                <TableHead className="font-semibold text-slate-600">Vehicule</TableHead>
                <TableHead className="font-semibold text-slate-600">Periode</TableHead>
                <TableHead className="font-semibold text-slate-600">Recettes</TableHead>
                <TableHead className="font-semibold text-slate-600">Attendu</TableHead>
                <TableHead className="font-semibold text-slate-600">Reparations</TableHead>
                <TableHead className="font-semibold text-slate-600">Benefice</TableHead>
                <TableHead className="font-semibold text-slate-600">Jours loue</TableHead>
                <TableHead className="font-semibold text-slate-600">Jours immo.</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bilan.map((b, i) => (
                <TableRow key={i} data-testid={`bilan-row-${i}`}>
                  <TableCell className="font-medium text-slate-900">{b.vehicule?.marque} {b.vehicule?.modele}<br/><span className="text-xs text-slate-500">{b.vehicule?.immatriculation}</span></TableCell>
                  <TableCell className="text-slate-600 text-sm">{b.periode?.debut} - {b.periode?.fin}</TableCell>
                  <TableCell className="text-emerald-600 font-medium">{b.total_recettes} Frc</TableCell>
                  <TableCell className="text-slate-600">{b.total_attendu} Frc</TableCell>
                  <TableCell className="text-red-600">{b.total_reparations} Frc</TableCell>
                  <TableCell className={`font-medium ${b.benefice >= 0 ? "text-emerald-600" : "text-red-600"}`}>{b.benefice} Frc</TableCell>
                  <TableCell className="text-slate-600">{b.jours_location}</TableCell>
                  <TableCell className="text-slate-600">{b.jours_immobilise}</TableCell>
                </TableRow>
              ))}
              {bilan.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">Aucune donnee pour cette periode</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
